import {
  ITEM_ICONS,
  TECHNIQUE_SCROLL_RARITY_ICONS,
} from "@ninja/asset-catalog";
import { RARITY_PRESENTATION } from "@ninja/domain";
import { ITEM_DEFINITION_VERSIONS } from "@ninja/game-content";
import { formatItemProgression } from "../../ui/itemProgression.js";

export interface LootItemPayload {
  dropId: string;
  itemId: string;
  name: string;
  quantity: number;
  rarity: string;
  tier: number;
}

export interface CombatLootPayload {
  eventId?: string;
  monsterId?: string;
  items?: LootItemPayload[];
}

export interface LootBatch {
  eventId: string;
  monsterName: string;
  receivedAt: number;
  items: LootItemPayload[];
}

const MAX_HISTORY_BATCHES = 40;
const MAX_REMEMBERED_EVENT_IDS = 500;
const MAX_QUEUED_NOTIFICATIONS = 20;
const TOAST_DURATION_MS = 3_600;
const EXIT_ANIMATION_MS = 180;

const ITEM_DEFINITIONS_BY_ID = new Map(
  ITEM_DEFINITION_VERSIONS.map((definition) => [definition.id, definition]),
);

export class LootEventLedger {
  private readonly batches: LootBatch[] = [];
  private readonly seenEventIds = new Set<string>();
  private readonly eventIdOrder: string[] = [];
  private fallbackEventSequence = 0;

  record(
    payload: CombatLootPayload,
    receivedAt = Date.now(),
  ): LootBatch | null {
    const items = (payload.items ?? []).filter(isValidLootItem);
    if (items.length === 0) return null;

    const suppliedEventId = payload.eventId?.trim();
    if (suppliedEventId && this.seenEventIds.has(suppliedEventId)) return null;

    const eventId =
      suppliedEventId ||
      `local-loot-${receivedAt}-${++this.fallbackEventSequence}`;
    if (suppliedEventId) this.rememberEventId(suppliedEventId);

    const batch: LootBatch = {
      eventId,
      monsterName: humanizeIdentifier(payload.monsterId) || "Monster",
      receivedAt,
      items: items.map((item) => ({ ...item })),
    };
    this.batches.unshift(batch);
    if (this.batches.length > MAX_HISTORY_BATCHES) this.batches.pop();
    return batch;
  }

  history(): readonly LootBatch[] {
    return this.batches;
  }

  private rememberEventId(eventId: string): void {
    this.seenEventIds.add(eventId);
    this.eventIdOrder.push(eventId);
    if (this.eventIdOrder.length > MAX_REMEMBERED_EVENT_IDS) {
      const expired = this.eventIdOrder.shift();
      if (expired) this.seenEventIds.delete(expired);
    }
  }
}

export class LootFeedbackController {
  private readonly ledger = new LootEventLedger();
  private readonly toastQueue: LootBatch[] = [];
  private readonly toastRegion = document.createElement("div");
  private readonly historyToggle = document.createElement("button");
  private readonly historyCount = document.createElement("span");
  private readonly historyPanel = document.createElement("section");
  private readonly historyList = document.createElement("ol");
  private currentToast: HTMLElement | undefined;
  private toastTimer: number | undefined;
  private exitTimer: number | undefined;
  private disposed = false;
  private historyOpen = false;

  constructor(
    private readonly root: HTMLElement,
    historyToggleHost?: HTMLElement,
  ) {
    this.root.className = "loot-feedback";
    this.root.setAttribute("aria-label", "Loot notifications and history");

    this.toastRegion.className = "loot-toast-region";
    this.toastRegion.setAttribute("aria-live", "polite");
    this.toastRegion.setAttribute("aria-relevant", "additions");
    this.toastRegion.setAttribute("aria-atomic", "false");

    this.historyToggle.type = "button";
    this.historyToggle.className = "loot-history-toggle";
    this.historyToggle.setAttribute("aria-expanded", "false");
    this.historyToggle.setAttribute("aria-controls", "loot-history-panel");
    this.historyToggle.addEventListener("click", this.toggleHistory);
    const historyIcon = document.createElement("img");
    historyIcon.src = "/assets/interface/loot-pouch.webp";
    historyIcon.alt = "";
    historyIcon.width = 32;
    historyIcon.height = 32;
    historyIcon.setAttribute("aria-hidden", "true");
    this.historyCount.className = "loot-history-count";
    this.historyCount.textContent = "0";
    this.historyToggle.append(historyIcon, this.historyCount);

    this.historyPanel.id = "loot-history-panel";
    this.historyPanel.className = "loot-history-panel";
    this.historyPanel.hidden = true;
    const historyHeading = document.createElement("h2");
    historyHeading.textContent = "Loot history";
    this.historyList.className = "loot-history-list";
    this.historyList.setAttribute("aria-live", "polite");
    this.historyPanel.append(historyHeading, this.historyList);

    if (historyToggleHost) {
      historyToggleHost.append(this.historyToggle);
      historyToggleHost.append(this.historyPanel);
      this.root.replaceChildren(this.toastRegion);
    } else {
      this.root.replaceChildren(
        this.toastRegion,
        this.historyToggle,
        this.historyPanel,
      );
    }
    this.renderHistory();
  }

  present(payload: CombatLootPayload): void {
    if (this.disposed) return;
    const batch = this.ledger.record(payload);
    if (!batch) return;

    this.renderHistory();
    if (this.toastQueue.length >= MAX_QUEUED_NOTIFICATIONS)
      this.toastQueue.shift();
    this.toastQueue.push(batch);
    this.presentNext();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.historyToggle.removeEventListener("click", this.toggleHistory);
    this.historyToggle.remove();
    this.historyPanel.remove();
    if (this.toastTimer !== undefined) window.clearTimeout(this.toastTimer);
    if (this.exitTimer !== undefined) window.clearTimeout(this.exitTimer);
    this.toastQueue.length = 0;
    this.root.replaceChildren();
  }

  private readonly toggleHistory = (): void => {
    this.historyOpen = !this.historyOpen;
    this.historyPanel.hidden = !this.historyOpen;
    this.historyToggle.setAttribute("aria-expanded", String(this.historyOpen));
  };

  private presentNext(): void {
    if (this.disposed || this.currentToast || this.toastQueue.length === 0)
      return;
    const batch = this.toastQueue.shift()!;
    const toast = document.createElement("article");
    toast.className = "loot-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-label", `Loot from ${batch.monsterName}`);

    const items = document.createElement("div");
    items.className = "loot-toast-items";
    const visibleItems = batch.items.slice(0, 2);
    for (const item of visibleItems) items.append(this.createItemRow(item));
    const hiddenItemsCount = batch.items.length - visibleItems.length;
    if (hiddenItemsCount > 0) {
      const moreItems = document.createElement("span");
      moreItems.className = "loot-item-more";
      moreItems.textContent = `+${hiddenItemsCount} more in loot history`;
      items.append(moreItems);
    }
    toast.append(items);

    this.toastRegion.replaceChildren(toast);
    this.currentToast = toast;
    this.toastTimer = window.setTimeout(() => {
      toast.classList.add("loot-toast-exit");
      this.exitTimer = window.setTimeout(() => {
        if (this.currentToast === toast) this.currentToast = undefined;
        toast.remove();
        this.toastTimer = undefined;
        this.exitTimer = undefined;
        this.presentNext();
      }, EXIT_ANIMATION_MS);
    }, TOAST_DURATION_MS);
  }

  private renderHistory(): void {
    const batches = this.ledger.history();
    this.historyCount.textContent = String(batches.length);
    this.historyCount.hidden = batches.length === 0;
    this.historyToggle.setAttribute(
      "aria-label",
      `Recent loot, ${batches.length} ${batches.length === 1 ? "drop" : "drops"}`,
    );
    if (batches.length === 0) {
      const empty = document.createElement("li");
      empty.className = "loot-history-empty";
      empty.textContent = "No items received in this session yet.";
      this.historyList.replaceChildren(empty);
      return;
    }

    const fragment = document.createDocumentFragment();
    for (const batch of batches) {
      for (const item of batch.items) {
        const entry = document.createElement("li");
        entry.className = "loot-history-entry";
        entry.style.setProperty("--loot-rarity", rarityColor(item.rarity));

        const details = document.createElement("div");
        details.className = "loot-history-entry-details";
        const name = document.createElement("strong");
        name.textContent = `${item.quantity}× ${itemDisplayName(item)}`;
        const meta = document.createElement("span");
        meta.textContent = [
          batch.monsterName,
          rarityLabel(item.rarity),
          formatItemProgression(item.tier),
        ]
          .filter(Boolean)
          .join(" · ");
        details.append(name, meta);

        const time = document.createElement("time");
        time.dateTime = new Date(batch.receivedAt).toISOString();
        time.textContent = new Date(batch.receivedAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        entry.append(this.createIcon(item), details, time);
        fragment.append(entry);
      }
    }
    this.historyList.replaceChildren(fragment);
  }

  private createItemRow(item: LootItemPayload): HTMLElement {
    const row = document.createElement("div");
    row.className = "loot-item-row";
    row.style.setProperty("--loot-rarity", rarityColor(item.rarity));
    row.append(this.createIcon(item));

    const details = document.createElement("div");
    details.className = "loot-item-details";
    const name = document.createElement("strong");
    name.textContent = itemDisplayName(item);
    const quality = document.createElement("span");
    quality.textContent = [
      rarityLabel(item.rarity),
      formatItemProgression(item.tier),
    ]
      .filter(Boolean)
      .join(" · ");
    details.append(name, quality);

    const quantity = document.createElement("span");
    quantity.className = "loot-item-quantity";
    quantity.textContent = `×${item.quantity}`;
    row.append(details, quantity);
    return row;
  }

  private createIcon(item: LootItemPayload): HTMLElement {
    const icon = document.createElement("span");
    icon.className = "loot-item-icon";
    icon.style.setProperty("--loot-rarity", rarityColor(item.rarity));
    const definition = ITEM_DEFINITIONS_BY_ID.get(item.itemId);
    const asset = definition
      ? ITEM_ICONS[definition.iconKey as keyof typeof ITEM_ICONS]
      : undefined;
    if (asset) {
      const image = document.createElement("img");
      image.src = `/${asset.path}`;
      image.alt = "";
      image.width = 40;
      image.height = 40;
      icon.append(image);
    } else {
      icon.textContent = itemDisplayName(item).slice(0, 1).toUpperCase();
      icon.setAttribute("aria-hidden", "true");
    }
    const isTechniqueScroll =
      definition?.category === "consumable" &&
      definition.usage?.kind === "technique_scroll";
    const scrollRarityIcon = isTechniqueScroll
      ? TECHNIQUE_SCROLL_RARITY_ICONS[
          item.rarity.toLocaleLowerCase() as keyof typeof TECHNIQUE_SCROLL_RARITY_ICONS
        ]
      : undefined;
    if (scrollRarityIcon) {
      const marker = document.createElement("img");
      marker.className = "loot-technique-scroll-marker";
      marker.src = `/${scrollRarityIcon.path}`;
      marker.alt = `${rarityLabel(item.rarity)} technique scroll`;
      marker.width = scrollRarityIcon.width;
      marker.height = scrollRarityIcon.height;
      icon.append(marker);
    }
    return icon;
  }
}

function isValidLootItem(item: LootItemPayload): boolean {
  return (
    typeof item?.itemId === "string" &&
    item.itemId.length > 0 &&
    Number.isInteger(item.quantity) &&
    item.quantity > 0 &&
    Number.isInteger(item.tier) &&
    item.tier > 0
  );
}

function itemDisplayName(item: LootItemPayload): string {
  const name = item.name?.trim();
  if (name) return name;
  return (
    ITEM_DEFINITIONS_BY_ID.get(item.itemId)?.name ??
    humanizeIdentifier(item.itemId)
  );
}

function rarityLabel(rarity: string): string {
  const normalized = rarity.toLowerCase() as keyof typeof RARITY_PRESENTATION;
  return (
    RARITY_PRESENTATION[normalized]?.label ?? RARITY_PRESENTATION.common.label
  );
}

function rarityColor(rarity: string): string {
  const normalized = rarity.toLowerCase() as keyof typeof RARITY_PRESENTATION;
  return (
    RARITY_PRESENTATION[normalized]?.color ?? RARITY_PRESENTATION.common.color
  );
}

function humanizeIdentifier(identifier?: string): string {
  if (!identifier) return "";
  return identifier
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
